<template>
  <div class="relative">
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('manage.payments-report.index')">Customer Payments</inertia-link>
      <span class="text-indigo-400 font-medium">/</span>
      Match Payments With Orders
    </h1>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
      <div>
        <h3>Choose Payments</h3>

        <div class="p-2 bg-white rounded flex justify-between my-2">
          <h4>Total Amount</h4>
          <h3>MVR {{ totalPaymentAmount.toLocaleString() }}</h3>
        </div>

        <table class="table bg-white mt-2 w-full">
          <thead>
            <tr class="border-b-2 border-gray-500">
              <th></th>
              <th class="py-1 px-2">Details</th>
              <th class="py-1 px-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in pending_payments" class="border-b border-gray-300 text-gray-700">
              <td class="py-1 px-2">
                <input type="checkbox" v-model="selectedPayments" :value="item.id">
              </td>
              <td class="py-2 px-2">
                <div class="text-base pb-1 text-gray-400">{{ item.id }}<span class="text-sm"> | {{ item.created_at | localDateTime}}</span></div>
                <div class="text-sm ">{{ item.description }}</div>
                <div v-if="item.source" class="py-2 text-xs">
                  <span class="bg-gray-200 rounded-lg p-1 px-2">{{ item.source }}</span>
                </div>
              </td>
              <td class="py-1 px-2 text-right">
                <div>MVR {{ item.amount }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="lg:-mt-10">
        <div class="flex justify-center gap-2 border-b mb-2">
          <div :class="{
            'py-2 px-2 rounded-t': true,
            'bg-primary-500 text-white cursor-default': true,
            'bg-gray-200 hover:bg-gray-300 text-gray-500 cursor-pointer': false
          }">Orders</div>
          <Link :class="{
            'py-2 px-2 rounded-t': true,
            'bg-primary-500 hover:bg-black-400 text-white cursor-default': false,
            'bg-gray-200 hover:bg-gray-300 text-gray-500 cursor-pointer': true
          }" :href="route('manage.payments-report.match-invoices')">Invoices</Link>
        </div>
        <div class="flex justify-between">
          <h3>Choose Orders</h3>

          <label>
            <input type="checkbox" v-model="filters.matchOrders">
            <span class="ml-2">Filter matching amount</span>
          </label>
        </div>

        <div class="px-2 bg-white rounded flex justify-between my-2">
          <div class="flex gap-4 p-2">
            <h4>Total Amount</h4>
            <h3>MVR {{ totalOrderAmount.toLocaleString() }}</h3>
          </div>
          <div>
            <button @click="confirmPaymentRecordForMatches" class="bg-blue-500 text-white px-2 h-full flex-1" v-if="totalPaymentAmount > 0 && totalOrderAmount === totalPaymentAmount">Record Payment</button>
          </div>
        </div>

        <div class="space-y-1 mt-2">
          <div v-for="match in OrdersList">

            <label class="relative block bg-white border rounded-lg shadow-sm px-6 py-2 cursor-pointer sm:flex sm:justify-between focus:outline-none">
              <input type="checkbox" :value="match.id"  v-model="matchingOrders">
              <div class="flex items-center mb-1 flex-grow pl-3">
                <div class="text-sm">
                  <p class="font-medium text-gray-700">Order #{{ match.id }}</p>
                  <p v-if="match.customer_account" class="font-large text-gray-900">{{ match.customer_account.name }}</p>
                  <div id="server-size-0-description-0" class="text-gray-500">
                    <p class="sm:inline">Created by {{ match.created_by.first_name }} {{ match.created_by.last_name }}</p>
                    <span class="hidden sm:inline sm:mx-1" aria-hidden="true">&middot;</span>
                    <p class="sm:inline"><span class="text-xs">📞</span> {{ match.created_by.mobile }}</p>
                    <p>{{ match.created_at | localDateTime }}</p>
                  </div>
                </div>
              </div>
              <div class="mt-2 flex text-sm sm:mt-0 sm:block sm:ml-4 sm:text-right">
                <div class="font-medium text-gray-900">{{ match.status.toUpperCase() }}</div>
                <div class="ml-1 mt-2 text-lg text-gray-500 sm:ml-0">MVR {{ match.quotation_amount.toLocaleString() }}</div>
              </div>
            </label>
          </div>
        </div>

      </div>
    </div>

    <div v-if="confirmRecordPayment" class="fixed z-10 bg-black opacity-25 left-0 right-0 top-0 bottom-0" @click="dismissConfirmPaymentModal"></div>
    <div v-if="confirmRecordPayment" class="absolute top-0 right-0 left-0 w-full mx-auto max-w-md bg-white shadow rounded overflow-hidden z-20">
      <div class="mb-2 px-2 py-3">
        <h1 class="text-xl">Please confirm the payment</h1>
      </div>
      <div class="px-2">
        <div class="text-base font-bold leading-loose">Matched Orders</div>
        <div v-for="order in selectedOrdersList" class="bg-white p-1">
          <div class="flex justify-between">
            <div class="text-gray-500 text-sm">
              <div>{{ order.id }} <span v-if="order.customer_account"> - {{ order.customer_account.name }}</span></div>
              <div class="text-xs">{{ order.created_at | localDate }} - {{ [order.created_by.first_name, order.created_by.last_name].join(' ') }}</div>
            </div>
            <div class="text-gray-500 text-sm">{{ order.quotation_amount }}</div>
          </div>
        </div>
        <div class="flex justify-between bg-gray-200 p-1 border-t-2 border-gray-400 text-sm">
          <div>Total Order Amount</div>
          <div>{{ totalOrderAmount }}</div>
        </div>
      </div>

      <div class="mt-1 px-2">
        <div class="text-base font-bold leading-loose">Matched Payments</div>
        <div v-for="payment in selectedPaymentList" class="bg-white p-1">
          <div class="flex justify-between">
            <div class="text-gray-500 text-sm">
              <div>{{ payment.description }}</div>
              <div v-if="payment.remarks" class="text-xs">Remarks: {{ payment.remarks }}</div>
            </div>
            <div class="text-gray-500 text-sm">{{ payment.amount }}</div>
          </div>
        </div>
        <div class="flex justify-between bg-gray-200 p-1 border-t-2 border-gray-400 text-sm">
          <div>Total Payment Amount</div>
          <div>{{ totalPaymentAmount }}</div>
        </div>
      </div>

      <div class="p-2 py-3 bg-gray-100 mt-2 flex justify-between">
        <button :disabled="processing" class="bg-gray-300 px-2 py-2 rounded" @click="cancelMatchingSelection">Cancel</button>
        <button v-if="!processing" class="bg-blue-500 px-2 py-2 rounded text-white" @click="recordPayment">Record Payment</button>
        <button v-else class="bg-blue-500 px-2 py-2 rounded text-white" @click="recordPayment">Processing</button>
      </div>
    </div>

  </div>

</template>

<script>
import Layout from '@/Shared/Admin/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import { Link } from "@inertiajs/inertia-vue";

export default {
  name: 'Show',
  layout: Layout,
  components: {
    LoadingButton,
    SelectInput,
    TextInput,
    Link
  },

  props: {
    pending_payments: {
      type: Array,
      required: true
    },
    pending_orders: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      filters: {
        matchOrders: true,
      },
      selectedPayments: [],
      matchingOrders: [],
      confirmRecordPayment: false,
      processing: false,
    }
  },

  methods: {
    confirmPaymentRecordForMatches() {
      this.confirmRecordPayment = true
    },

    dismissConfirmPaymentModal() {
      this.confirmRecordPayment = false
    },

    cancelMatchingSelection() {
      this.confirmRecordPayment = false;
      this.selectedPayments = [];
      this.matchingOrders = [];
    },

    recordPayment() {
      this.processing = true;
      this.$inertia.post(this.route('manage.payments-report.match-record-payment'), {
        payments: this.selectedPayments,
        orders: this.matchingOrders
      }, {
        preserveState: false,
        onFinish: () => {
          this.confirmRecordPayment = false;
          this.selectedPayments = [];
          this.matchingOrders = [];
          this.processing = false;
        }
      })
    }
  },

  computed: {
    totalPaymentAmount() {
      // from selectedPayments calculate total amount
      return this.selectedPayments.reduce((acc, curr) => {
        return acc + this.pending_payments.find(payment => payment.id === curr).amount
      }, 0)
    },
    totalOrderAmount() {
      // from matchingOrders calculate total amount
      return this.matchingOrders.reduce((acc, curr) => {
        return acc + this.pending_orders.find(order => order.id === curr).quotation_amount
      }, 0)
    },

    selectedPaymentList() {
      return this.pending_payments.filter(payment => this.selectedPayments.includes(payment.id))
    },

    selectedOrdersList() {
      return this.pending_orders.filter(order => this.matchingOrders.includes(order.id))
    },

    OrdersList() {
      if (this.filters.matchOrders === true && this.totalPaymentAmount > 0) {
        return this.pending_orders.filter((item) => item.quotation_amount === this.totalPaymentAmount)
      } else {
        return this.pending_orders
      }
    }
  }
}
</script>

